'use client';

import { useEffect, useRef, useState } from 'react';
import { parseGrafanaRange } from '../grafana-range';

interface Props {
  onApply: (fromMs: number, toMs: number) => void;
  setLiveTail: (b: boolean) => void;
}

// A small inline popover for pasting a Grafana time-range fragment
// (e.g. "from=1781042361555&to=1781049375202") into the From/To selectors.
// Closes on outside click or Escape; pressing Enter in the input applies.
export function PasteGrafanaRangeButton({ onApply, setLiveTail }: Props) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    const onDocClick = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const apply = () => {
    const parsed = parseGrafanaRange(input);
    if (!parsed) {
      setError('Could not parse from/to from that input.');
      return;
    }
    setLiveTail(false);
    onApply(parsed.fromMs, parsed.toMs);
    setInput('');
    setError(null);
    setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => {
          if (!open) { setInput(''); setError(null); } // opening: start fresh
          setOpen((o) => !o);
        }}
        title="Paste a Grafana from=…&to=… fragment"
        className="h-9 rounded-md border bg-background px-2 text-sm hover:bg-muted"
      >
        Paste Grafana
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Paste Grafana range"
          className="absolute right-0 top-full z-40 mt-2 w-80 rounded-md border bg-background p-3 shadow-md"
        >
          <label className="block text-xs text-muted-foreground mb-1">
            Paste a Grafana <code>from=…&amp;to=…</code> fragment
          </label>
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              if (error) setError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                apply();
              }
            }}
            placeholder="from=1781042361555&to=1781049375202"
            className="w-full rounded-md border bg-background px-2 py-1 text-sm font-mono"
          />
          {error && (
            <p role="alert" className="mt-1 text-xs text-red-400">
              {error}
            </p>
          )}
          <div className="mt-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setError(null);
              }}
              className="rounded-md border bg-background px-2 py-1 text-xs hover:bg-muted"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={apply}
              className="rounded-md border border-primary bg-primary px-2 py-1 text-xs text-primary-foreground hover:opacity-90"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
